// console.log('fjw-photo-grid.js loaded');

const gridItemAlreadyDefined = customElements.get('grid-item');
const gridGalleryAlreadyDefined = customElements.get('grid-gallery');

// MasonryGrid Class
class MasonryGrid {
    constructor(container, options = {}) {
        // console.log('MasonryGrid initialized with container:', container);
        this.container = container;
        this.options = {
            gridItemClass: 'grid-item',
            captionClass: 'grid-item-caption',
            exifClass: 'grid-item-exif',
            columns: 4,
            maxAspectRatioDistortion: 0.3,
            extractExif: true,
            ...options
        };

        if (this.container) {
            this.observer = new MutationObserver(this.onMutation.bind(this));
        }
    }

    render(images) {
        if (!this.container) {
            console.error('Container not found');
            return;
        }

        // console.log('Rendering grid with images:', images);

        if (!images || images.length === 0) {
            console.warn('No images provided to render');
            return;
        }

        this.container.classList.add('masonry-grid');
        this.container.innerHTML = '';

        this.preloadAndRenderImages(images);
    }

    async preloadAndRenderImages(imageData) {
        // console.log('Preloading images:', imageData);
        const imageDataWithAspectRatios = [];

        for (let i = 0; i < imageData.length; i++) {
            const img = imageData[i];
            let aspectRatio = img.aspectRatio;

            if (!aspectRatio) {
                const image = new Image();
                image.src = img.src;
                await new Promise((resolve, reject) => {
                    image.onload = () => {
                        aspectRatio = image.naturalWidth / image.naturalHeight;
                        resolve();
                    };
                    image.onerror = () => {
                        console.warn(`Failed to load image ${img.src} to determine dimensions.`);
                        aspectRatio = 1;
                        resolve();
                    };
                });
            }

            imageDataWithAspectRatios.push({
                ...img,
                aspectRatio: aspectRatio
            });
        }

        this.renderWithAspectRatios(imageDataWithAspectRatios);
    }

    generateSmartPatterns(imageData) {
        // console.log('Generating smart patterns for images');
        const patterns = [];

        // Determine appropriate pattern based on aspect ratio
        imageData.forEach(img => {
            const aspectRatio = img.aspectRatio || 1;

            // Log detailed aspect ratio information for debugging
            // console.log(`Image aspect ratio: ${aspectRatio.toFixed(3)}`);

            // Simplified classification based on standard aspect ratios
            let sizeClass;

            // Standard classifications - more intuitive boundaries
            if (aspectRatio >= 1.6) {
                // Wide formats (16:9, 2:1, etc.) - spans 2 columns
                sizeClass = 'grid-item-wide';
            } else if (aspectRatio >= 0.9 && aspectRatio <= 1.1) {
                // Near-square formats (1:1) - spans 2 columns, 2 rows
                sizeClass = 'grid-item-large';
            } else if (aspectRatio > 1.1 && aspectRatio < 1.6) {
                // Common landscape formats (4:3, 3:2) - spans 2 columns
                sizeClass = 'grid-item-landscape';
            } else if (aspectRatio <= 0.9) {
                // Portrait formats (2:3, 3:4) - spans 1 column, 2 rows
                sizeClass = 'grid-item-portrait';
            } else {
                // Fallback
                sizeClass = 'grid-item-small';
            }

            // Special case for extreme aspect ratios
            if (aspectRatio >= 2.2) {
                // Panoramic formats
                sizeClass = 'grid-item-panorama';
            } else if (aspectRatio <= 0.45) {
                // Extremely tall/narrow formats
                sizeClass = 'grid-item-tall';
            }

            // console.log(`Classified as: ${sizeClass}`);
            patterns.push(sizeClass);
        });

        return patterns;
    }

    renderWithAspectRatios(imageData) {
        // console.log('Rendering with aspect ratios:', imageData);
        const patterns = this.generateSmartPatterns(imageData);

        // Special handling for single-image galleries - make them full width
        if (this.container && imageData.length === 1) {
            // console.log('Single image gallery - applying full-width layout');
            // Override the pattern to make any single image take full width
            patterns[0] = 'grid-item-panorama';
        }
        // Check if this is a two-image gallery
        else if (this.container && imageData.length === 2) {
            // Calculate variance in aspect ratios to determine layout approach
            const aspectRatios = imageData.map(img => img.aspectRatio);
            const avgAspect = aspectRatios.reduce((a, b) => a + b, 0) / aspectRatios.length;
            const variance = aspectRatios.reduce((acc, ratio) =>
                acc + Math.abs(ratio - avgAspect), 0) / aspectRatios.length;

            // If aspect ratios are similar, make them share space evenly
            if (variance < 0.25) {
                // Similar aspects - use even columns
                patterns[0] = patterns[0] === 'grid-item-portrait' || patterns[0] === 'grid-item-tall'
                    ? 'grid-item-portrait' : 'grid-item-landscape';
                patterns[1] = patterns[1] === 'grid-item-portrait' || patterns[1] === 'grid-item-tall'
                    ? 'grid-item-portrait' : 'grid-item-landscape';
            } else {
                // Dissimilar aspects - use natural sizing
                this.aspectPreservationNeeded = true;

                // Ensure we correctly classify each image based on its actual aspect ratio
                for (let i = 0; i < imageData.length; i++) {
                    const aspect = imageData[i].aspectRatio;
                    if (aspect >= 1.3) { // Landscape or wider
                        patterns[i] = aspect >= 1.8 ? 'grid-item-wide' : 'grid-item-landscape';
                    } else if (aspect <= 0.85) { // Portrait or taller
                        patterns[i] = aspect <= 0.6 ? 'grid-item-tall' : 'grid-item-portrait';
                    } else { // Near square
                        patterns[i] = 'grid-item-small';
                    }
                }
            }
        }
        // Handle three-image galleries - ALL three-image galleries should get the three-image-gallery class
        else if (this.container && imageData.length === 3) {
            // Calculate variance in aspect ratios to determine layout approach
            const aspectRatios = imageData.map(img => img.aspectRatio);
            const avgAspect = aspectRatios.reduce((a, b) => a + b, 0) / aspectRatios.length;
            const variance = aspectRatios.reduce((acc, ratio) =>
                acc + Math.abs(ratio - avgAspect), 0) / aspectRatios.length;

            // If variance is high, respect original aspects
            const shouldRespectAspect = variance > 0.25;

            // Flag will be used to determine if we should preserve natural aspects
            this.aspectPreservationNeeded = shouldRespectAspect;

            // IMPORTANT: Mark ALL three-image galleries with the three-image-gallery class
            if (this.container) {
                this.container.classList.add('three-image-gallery');
            }

            if (shouldRespectAspect) {
                // Handle mixed aspect ratios with respect-aspect class
                let landscapeCount = 0;
                let portraitCount = 0;

                // Analyze the actual aspect ratios and classify accordingly
                for (let i = 0; i < imageData.length; i++) {
                    const aspect = imageData[i].aspectRatio;
                    if (aspect >= 1.3) { // Landscape or wider
                        patterns[i] = aspect >= 1.8 ? 'grid-item-wide' : 'grid-item-landscape';
                        landscapeCount++;
                    } else if (aspect <= 0.85) { // Portrait or taller
                        patterns[i] = aspect <= 0.6 ? 'grid-item-tall' : 'grid-item-portrait';
                        portraitCount++;
                    } else { // Near square
                        patterns[i] = 'grid-item-small';
                        landscapeCount++; // Count squares as landscape for layout purposes
                    }
                }
            } else {
                // For regular 3-image galleries with similar aspect ratios,
                // Make them use built-in grid classes that are guaranteed to work
                patterns[0] = 'grid-item-landscape';
                patterns[1] = 'grid-item-landscape';
                patterns[2] = 'grid-item-landscape';
            }
        }

        const duplicates = [];
        patterns.forEach((pattern, i) => {
            if (pattern && typeof pattern === 'object' && pattern.isDuplicate) {
                duplicates.push(pattern);
            }
        });

        imageData.forEach((img, index) => {
            const patternItem = patterns[index];
            if (typeof patternItem === 'object') return;

            const sizeClass = patternItem;

            const gridItem = document.createElement('div');
            gridItem.classList.add(this.options.gridItemClass);
            gridItem.classList.add(sizeClass);

            // Add a class to respect original aspect ratio if needed
            if (this.aspectPreservationNeeded) {
                gridItem.classList.add('respect-aspect');
            }

            const figure = document.createElement('figure');
            const a = document.createElement('a');

            let thumbnailWidth, lightboxWidth;

            if (sizeClass === 'grid-item-small') {
                thumbnailWidth = 500;
            } else if (sizeClass === 'grid-item-landscape' || sizeClass === 'grid-item-portrait') {
                thumbnailWidth = 800;
            } else if (sizeClass === 'grid-item-wide' || sizeClass === 'grid-item-tall') {
                thumbnailWidth = 1000;
            } else if (sizeClass === 'grid-item-large') {
                thumbnailWidth = 1200;
            } else if (sizeClass === 'grid-item-panorama') {
                thumbnailWidth = 1400;
            }

            lightboxWidth = 2000;

            const lightboxUrl = this.getOptimizedShopifyUrl(img.src, {
                width: lightboxWidth,
                crop: 'center'
            });

            a.href = lightboxUrl;
            a.setAttribute('data-fancybox', img.fancybox || '');

            // Build a complete caption with EXIF data included
            let captionHTML = '';
            if (img.caption) {
                captionHTML = `<div class="caption-title">${img.caption}</div>`;
            }

            // Add EXIF data to the caption if available - with improved formatting
            if (img.exif) {
                const exif = img.exif;
                const exifData = [];

                if (exif.make || exif.model) {
                    exifData.push(`<span class="exif-item"><strong>Camera:</strong> ${(exif.make || '') + ' ' + (exif.model || '')}</span>`);
                }
                // Fix the aperture value to remove double "f/" prefix
                if (exif.aperture) {
                    const apertureValue = exif.aperture.toString().startsWith('f/') ?
                        exif.aperture :
                        `f/${exif.aperture}`;
                    exifData.push(`<span class="exif-item"><strong>Aperture:</strong> ${apertureValue}</span>`);
                }
                if (exif.shutter) exifData.push(`<span class="exif-item"><strong>Shutter:</strong> ${exif.shutter}</span>`);
                if (exif.iso) exifData.push(`<span class="exif-item"><strong>ISO:</strong> ${exif.iso}</span>`);
                if (exif.focal) exifData.push(`<span class="exif-item"><strong>Focal Length:</strong> ${exif.focal}mm</span>`);

                if (exifData.length > 0) {
                    captionHTML += `<div class="caption-exif">${exifData.join(' ')}</div>`;
                }
            }

            // Set the complete caption as a single data attribute
            if (captionHTML) {
                a.setAttribute('data-caption', captionHTML);
            }

            const image = document.createElement('img');

            const smallUrl = this.getOptimizedShopifyUrl(img.src, {
                width: Math.round(thumbnailWidth * 0.5),
                crop: 'center'
            });

            const mediumUrl = this.getOptimizedShopifyUrl(img.src, {
                width: thumbnailWidth,
                crop: 'center'
            });

            const largeUrl = this.getOptimizedShopifyUrl(img.src, {
                width: Math.round(thumbnailWidth * 1.5),
                crop: 'center'
            });

            image.src = mediumUrl;

            image.srcset = `${smallUrl} ${Math.round(thumbnailWidth * 0.5)}w, ${mediumUrl} ${thumbnailWidth}w, ${largeUrl} ${Math.round(thumbnailWidth * 1.5)}w`;

            if (sizeClass === 'grid-item-small') {
                image.sizes = '(max-width: 767px) 50vw, 25vw';
            } else if (sizeClass === 'grid-item-landscape' || sizeClass === 'grid-item-portrait') {
                image.sizes = '(max-width: 767px) 50vw, 33vw';
            } else if (sizeClass === 'grid-item-wide' || sizeClass === 'grid-item-tall') {
                image.sizes = '(max-width: 767px) 100vw, 50vw';
            } else if (sizeClass === 'grid-item-large') {
                image.sizes = '(max-width: 767px) 100vw, 50vw';
            } else if (sizeClass === 'grid-item-panorama') {
                image.sizes = '(max-width: 767px) 100vw, 75vw';
            }

            image.alt = img.alt || '';
            image.title = img.caption || ''; // Use caption for the title attribute too
            image.loading = "lazy";

            a.appendChild(image);
            figure.appendChild(a);
            gridItem.appendChild(figure);

            this.extractExifData(a, img);

            if (img.caption || img.alt) {
                const caption = document.createElement('div');
                caption.classList.add(this.options.captionClass);
                caption.textContent = img.caption || img.alt;
                gridItem.appendChild(caption);
            }

            this.container.appendChild(gridItem);
        });

        duplicates.forEach(duplicate => {
            const originalImg = imageData[duplicate.originalIndex];
            const sizeClass = duplicate.sizeClass;

            const gridItem = document.createElement('div');
            gridItem.classList.add(this.options.gridItemClass);
            gridItem.classList.add(sizeClass);
            gridItem.classList.add('grid-item-duplicate');

            if (duplicate.position) {
                gridItem.style.gridRow = `${duplicate.position.row + 1}`;
                gridItem.style.gridColumn = `${duplicate.position.col + 1}`;
            }

            const figure = document.createElement('figure');
            const a = document.createElement('a');
            const lightboxUrl = this.getOptimizedShopifyUrl(originalImg.src, {
                width: 2000,
                crop: 'center'
            });

            a.href = lightboxUrl;
            a.setAttribute('data-fancybox', originalImg.fancybox || '');

            const image = document.createElement('img');
            image.src = this.getOptimizedShopifyUrl(originalImg.src, {
                width: 800,
                crop: 'center'
            });

            image.alt = originalImg.alt || '';
            image.title = originalImg.title || '';
            image.loading = "lazy";

            a.appendChild(image);
            figure.appendChild(a);
            gridItem.appendChild(figure);

            this.container.appendChild(gridItem);
        });

        if (this.container) {
            this.observer.observe(this.container, { childList: true, subtree: false });
        }
    }

    getOptimizedShopifyUrl(url, options = {}) {
        if (!url || typeof url !== 'string') {
            console.warn('Invalid URL provided to getOptimizedShopifyUrl:', url);
            return url;
        }

        // Check if it's a Shopify CDN URL
        if (!url.includes('cdn.shopify.com')) {
            return url;
        }

        // Split the URL at the extension
        const extensionMatch = url.match(/\.(jpg|jpeg|png|gif|webp)($|\?)/i);
        if (!extensionMatch) {
            return url;
        }

        const extension = extensionMatch[1];
        const urlParts = url.split(`.${extension}`);

        // Build the parameters string
        let paramsStr = '';
        if (options.width) {
            paramsStr += `_${options.width}x`;
        }
        if (options.crop) {
            paramsStr += `_crop_${options.crop}`;
        }

        // Combine the parts back together
        return `${urlParts[0]}${paramsStr}.${extension}${urlParts[1] || ''}`;
    }

    extractExifData(element, img) {
        if (!this.options.extractExif || !img.exif) {
            return;
        }

        const exif = img.exif;
        const exifData = [];

        // Add EXIF data
        if (exif.make || exif.model) {
            exifData.push(`<strong>Camera:</strong> ${(exif.make || '') + ' ' + (exif.model || '')}`);
        }
        // Fix the aperture value here too to avoid double "f/"
        if (exif.aperture) {
            const apertureValue = exif.aperture.toString().startsWith('f/') ?
                exif.aperture :
                `f/${exif.aperture}`;
            exifData.push(`<strong>Aperture:</strong> ${apertureValue}`);
        }
        if (exif.shutter) exifData.push(`<strong>Shutter:</strong> ${exif.shutter}`);
        if (exif.iso) exifData.push(`<strong>ISO:</strong> ${exif.iso}`);
        if (exif.focal) exifData.push(`<strong>Focal Length:</strong> ${exif.focal}mm`);

        // Only create EXIF display if we have data
        if (exifData.length > 0) {
            const exifContainer = document.createElement('div');
            exifContainer.classList.add(this.options.exifClass);
            exifContainer.innerHTML = exifData.join(' | ');

            element.parentNode.appendChild(exifContainer);
        }
    }

    initFancybox() {
        // Only initialize if window.Fancybox exists
        if (typeof window.Fancybox !== 'undefined') {
            // Use Fancybox's built-in caption functionality which supports HTML
            window.Fancybox.bind(this.container.querySelectorAll('[data-fancybox]'), {
                Image: {
                    zoom: true,
                },
                Thumbs: {
                    autoStart: true,
                }
            });
        } else {
            console.warn('Fancybox is not loaded, skipping initialization');
        }
    }

    onMutation(mutations) {
        try {
            if (!this.container) return;

            const galleryItems = this.container.querySelectorAll('[data-fancybox]');
            if (galleryItems.length > 0) {
                this.initFancybox();
            }
        } catch (error) {
            console.warn('Error in MasonryGrid mutation handler:', error);
        }
    }
}

// Define our custom elements with the new names
if (!gridItemAlreadyDefined) {
    class GridItem extends HTMLElement {
        connectedCallback() {
            // console.log('GridItem connected');
        }
    }

    try {
        customElements.define('grid-item', GridItem);
        // console.log('Defined custom element: grid-item');
    } catch (e) {
        console.warn('Could not define grid-item:', e);
    }
} else {
    // console.log('Using already defined grid-item element');
}

if (!gridGalleryAlreadyDefined) {
    class GridGallery extends HTMLElement {
        constructor() {
            super();
            // console.log('GridGallery constructor called');
            this.displayOrder = "fixed";
        }

        static get observedAttributes() {
            return ['image-data', 'extract-exif', 'display-order'];
        }

        attributeChangedCallback(name, oldValue, newValue) {
            if (name === 'image-data') {
                try {
                    this.imageData = JSON.parse(newValue);
                    this.loadAndRenderGallery();
                } catch (e) {
                    console.error('Error parsing image-data', e);
                }
            }
            if (name === 'extract-exif') {
                this.extractExif = newValue === 'true';
                this.loadAndRenderGallery();
            }
            if (name === 'display-order') {
                this.displayOrder = newValue || 'fixed';
                this.loadAndRenderGallery();
            }
        }

        connectedCallback() {
            this.displayOrder = this.getAttribute('display-order') || 'fixed';
            this.loadAndRenderGallery();
        }

        async loadAndRenderGallery() {
            // console.log('loadAndRenderGallery called');
            let galleryItems = Array.from(this.querySelectorAll('grid-item'));
            // console.log('Found gallery items:', galleryItems.length);

            if (galleryItems.length === 0) {
                console.warn('No grid-item elements found in GridGallery');
                return;
            }

            let imageData = [];
            const imageDimensions = [];

            if (this.displayOrder === 'random') {
                galleryItems = this.shuffleArray(galleryItems);
            }

            try {
                for (const item of galleryItems) {
                    const src = item.getAttribute('src');
                    if (!src) {
                        console.error('Gallery item missing src attribute:', item);
                        continue;
                    }

                    try {
                        const dimensions = await getImageDimensions(src);
                        imageDimensions.push(dimensions);
                    } catch (err) {
                        console.error(`Error loading image dimensions for ${src}:`, err);
                        imageDimensions.push({ width: 1, height: 1 });
                    }
                }

                imageData = galleryItems.map((item, index) => {
                    const src = item.getAttribute('src');
                    const alt = item.getAttribute('alt') || '';
                    const caption = item.getAttribute('caption') || item.getAttribute('title') || '';
                    const fancybox = this.getAttribute('gallery-id') || 'gallery';

                    const dimensions = imageDimensions[index] || { width: 1, height: 1 };
                    const { width, height } = dimensions;
                    const aspectRatio = width / height;

                    const exif = {};
                    const getAttributeValue = (attrName) => item.getAttribute(attrName) || '';

                    exif.make = getAttributeValue('make');
                    exif.model = getAttributeValue('model');
                    exif.aperture = getAttributeValue('aperture');
                    exif.shutter = getAttributeValue('shutterSpeed');
                    exif.iso = getAttributeValue('iso');
                    exif.focal = getAttributeValue('focal-length');
                    exif.dateTimeOriginal = getAttributeValue('date-time-original');
                    exif.lensMake = getAttributeValue('lens-make');
                    exif.lensModel = getAttributeValue('lens-model');
                    exif.software = getAttributeValue('software');
                    exif.copyright = getAttributeValue('copyright');
                    exif.GPSLatitude = getAttributeValue('gps-latitude');
                    exif.GPSLongitude = getAttributeValue('gps-longitude');
                    exif.meteringMode = getAttributeValue('metering-mode');
                    exif.flash = getAttributeValue('flash');
                    exif.whiteBalance = getAttributeValue('white-balance');
                    exif.ApertureValue = getAttributeValue('aperture-value');
                    exif.ShutterSpeedValue = getAttributeValue('shutter-speed-value');
                    exif.ExposureBiasValue = getAttributeValue('exposure-bias-value');
                    exif.MaxApertureValue = getAttributeValue('max-aperture-value');
                    exif.SubjectDistance = getAttributeValue('subject-distance');
                    exif.SceneCaptureType = getAttributeValue('scene-capture-type');

                    // Add debug output to check if EXIF attributes are being read
                    // console.log(`EXIF data from item ${index}:`, {
                    //     make: exif.make,
                    //     model: exif.model,
                    //     aperture: exif.aperture,
                    //     shutter: exif.shutter,
                    //     iso: exif.iso,
                    //     focal: exif.focal
                    // });

                    const hasExif = exif.make || exif.model || exif.aperture || exif.shutter ||
                        exif.iso || exif.focal;

                    return {
                        src,
                        alt,
                        title: caption, // Store caption in title field for internal use
                        caption,        // Also store in caption field
                        fancybox,
                        aspectRatio,
                        exif: hasExif ? exif : null  // Only include exif if we have data
                    };
                });

                // Try to extract EXIF data from images if available and supported
                if (window.extractImageExif) {
                    for (let i = 0; i < imageData.length; i++) {
                        if (!imageData[i].exif) {
                            try {
                                // console.log('Attempting to extract EXIF data for image:', imageData[i].src);
                                const extractedExif = await window.extractImageExif(imageData[i].src);

                                if (extractedExif && Object.keys(extractedExif).length > 0) {
                                    // console.log('Successfully extracted EXIF data:', extractedExif);

                                    // Create a simplified exif object with the properties we want
                                    imageData[i].exif = {
                                        make: extractedExif.make,
                                        model: extractedExif.model,
                                        aperture: extractedExif.aperture,
                                        shutter: extractedExif.exposureTime || extractedExif.shutterSpeedValue,
                                        iso: extractedExif.iso,
                                        focal: extractedExif.focalLength
                                    };
                                }
                            } catch (error) {
                                // Silence the annoying "Not valid TIFF data!" errors in the console
                                console.debug('EXIF extraction skipped or failed silently for', imageData[i].src);
                            }
                        }
                    }
                }

                this.renderGallery(imageData);
            } catch (err) {
                console.error('Error in loadAndRenderGallery:', err);
            }
        }

        shuffleArray(array) {
            const newArray = [...array];
            for (let i = newArray.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
            }
            return newArray;
        }

        renderGallery(imageData) {
            // console.log('Rendering gallery with data:', imageData);

            if (!imageData || imageData.length === 0) {
                console.warn('No image data to render gallery');
                return;
            }

            this.galleryId = this.getAttribute('gallery-id') || 'gallery-' + Math.random().toString(36).substring(7);

            this.innerHTML = '';

            const container = document.createElement('div');
            container.id = this.galleryId;
            container.className = 'masonry-grid';
            this.appendChild(container);

            if (!this.querySelector('.masonry-grid')) {
                console.error('Container not attached to DOM');
                this.appendChild(container);
            }

            const masonryGrid = new MasonryGrid(container, {
                gridItemClass: 'grid-item',
                captionClass: 'grid-item-caption',
                exifClass: 'grid-item-exif',
                columns: 4,
                maxAspectRatioDistortion: 0.3,
                extractExif: this.extractExif === true,
                displayOrder: this.displayOrder
            });

            setTimeout(() => {
                masonryGrid.render(imageData);
            }, 50);
        }
    }

    try {
        customElements.define('grid-gallery', GridGallery);
        // console.log('Defined custom element: grid-gallery');
    } catch (e) {
        console.warn('Could not define grid-gallery:', e);
    }
} else {
    // console.log('Using already defined grid-gallery element');
}

// Export for module usage scenarios
if (typeof window !== 'undefined') {
    window.MasonryGrid = MasonryGrid;
    window.GridGallery = gridGalleryAlreadyDefined ?
        customElements.get('grid-gallery').constructor :
        customElements.get('grid-gallery').constructor;

    window.initPhotoGalleries = function () {
        try {
            const galleries = document.querySelectorAll('grid-gallery');
            // console.log('Initializing', galleries.length, 'galleries');

            galleries.forEach(gallery => {
                if (typeof gallery.loadAndRenderGallery === 'function') {
                    gallery.loadAndRenderGallery();
                }
            });
        } catch (e) {
            console.error('Error initializing galleries:', e);
        }
    };
}

// Add a load event as a fallback for complete rendering
window.addEventListener('load', () => {
    // console.log('Window load event fired - ensuring all galleries are initialized');

    try {
        const galleries = document.querySelectorAll('grid-gallery');
        galleries.forEach(gallery => {
            const gridItems = gallery.querySelectorAll('.grid-item');
            if (gridItems.length === 0 && typeof gallery.loadAndRenderGallery === 'function') {
                // console.log('Gallery has no grid items after load, re-initializing');
                gallery.loadAndRenderGallery();
            }
        });
    } catch (e) {
        console.error('Error in window load handler:', e);
    }
});

// Helper function for getting image dimensions
async function getImageDimensions(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        let timeout;

        img.onload = () => {
            clearTimeout(timeout);
            resolve({
                width: img.naturalWidth || 1,
                height: img.naturalHeight || 1
            });
        };

        img.onerror = (err) => {
            clearTimeout(timeout);
            console.warn(`Failed to load image ${src} to determine dimensions.`);
            reject(err);
        };

        timeout = setTimeout(() => {
            console.warn(`Timeout loading image ${src}`);
            resolve({ width: 1, height: 1 });
        }, 5000);

        img.src = src;
    });
}

// We need to check if a gallery has the "data-preserve-layout" attribute
window.addEventListener('DOMContentLoaded', () => {
    // Find product galleries that should use the more graceful WestcottU-style layout
    const productGalleries = document.querySelectorAll('.product-grid-gallery, [data-preserve-layout="true"]');

    productGalleries.forEach(gallery => {
        if (gallery.tagName.toLowerCase() === 'grid-gallery') {
            // Set the attribute directly on the grid-gallery element
            gallery.setAttribute('data-preserve-layout', 'true');
        } else {
            // For container elements, find child grid-gallery elements
            const gridGalleries = gallery.querySelectorAll('grid-gallery');
            gridGalleries.forEach(gridGallery => {
                gridGallery.setAttribute('data-preserve-layout', 'true');
            });
        }
    });
});